// The review queue: unpublished changes, ones assigned to you, and content due for an accuracy check. Plus the tree markers
import { createContext, useState } from "react"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { cn } from "@/lib/utils"
import { BadgeCheck, CalendarClock, CircleDot, Upload } from "lucide-react"

export interface PendingItem {
  key: string
  title: string
  kind: string
  summary: string
  // Who's been asked to review it, when someone has
  reviewer?: string
  assignedToMe?: boolean
}

export interface DueItem {
  key: string
  title: string
  kind: string
  // e.g. "Last checked 8 months ago"
  detail: string
  overdue: boolean
}

type Tab = "pending" | "mine" | "due"

export function PendingChangesButton({ items, due = [], canPublish, onPublish, onPublishAll, onDiscard, onOpen, onVerify }: {
  items: PendingItem[]
  due?: DueItem[]
  canPublish: boolean
  onPublish: (key: string) => void
  onPublishAll: () => void
  onDiscard: (key: string) => void
  onOpen: (key: string) => void
  onVerify?: (key: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Tab>("pending")
  const mine = items.filter(i => i.assignedToMe)
  const overdue = due.filter(d => d.overdue).length
  if (!items.length && !due.length) return null

  const parts = [
    items.length && `${items.length} unpublished`,
    mine.length && `${mine.length} for you`,
    due.length && `${due.length} due for review`,
  ].filter(Boolean)

  const openTab = (t: Tab) => { setTab(t); setOpen(true) }
  const list = tab === "mine" ? mine : items

  return (
    <>
      <button
        type="button"
        onClick={() => openTab(mine.length ? "mine" : items.length ? "pending" : "due")}
        className={cn(
          "flex shrink-0 items-center gap-2 border-t px-3 py-2 text-left text-xs hover:bg-muted/60",
          mine.length || overdue ? "text-amber-800 dark:text-amber-200" : "text-sky-800 dark:text-sky-200",
        )}
      >
        <CircleDot className="h-3.5 w-3.5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{parts.join(" · ")}</span>
        <span className="text-muted-foreground">Review</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
          <SheetHeader className="space-y-3 border-b p-4">
            <SheetTitle>Review</SheetTitle>
            <div className="flex gap-1">
              {([
                ["pending", `Unpublished (${items.length})`],
                ["mine", `Assigned to me (${mine.length})`],
                ["due", `Due for review (${due.length})`],
              ] as const).map(([t, label]) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={cn(
                    "rounded px-2 py-1 text-xs transition-colors",
                    tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <SheetDescription>
              {tab === "due"
                ? "Live content not checked within its review interval. Open it to check it, then mark it verified."
                : "Saved edits that aren't live yet. The public site and agents still see the published version."}
              {tab !== "due" && !canPublish && " A manager publishes them."}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {tab !== "due" && !list.length && (
              <p className="p-4 text-sm text-muted-foreground">{tab === "mine" ? "Nothing assigned to you." : "No unpublished changes."}</p>
            )}
            {tab !== "due" && list.map(item => (
              <div key={item.key} className="flex items-start gap-2 border-b px-4 py-2.5">
                <button type="button" className="min-w-0 flex-1 text-left" onClick={() => { setOpen(false); onOpen(item.key) }}>
                  <p className="truncate text-sm font-medium">{item.title}</p>
                  <p className="text-[11px] text-muted-foreground">
                    <span className="capitalize">{item.kind}</span> · {item.summary}
                  </p>
                  {item.reviewer && (
                    <p className="text-[11px] font-medium text-amber-700 dark:text-amber-300">Awaiting review · {item.reviewer}</p>
                  )}
                </button>
                <Button size="sm" variant="ghost" className="h-7 shrink-0 px-2 text-xs" onClick={() => onDiscard(item.key)}>Discard</Button>
                {canPublish && (
                  <Button size="sm" variant="outline" className="h-7 shrink-0 px-2 text-xs" onClick={() => onPublish(item.key)}>Publish</Button>
                )}
              </div>
            ))}

            {tab === "due" && !due.length && <p className="p-4 text-sm text-muted-foreground">Everything live has been checked recently.</p>}
            {tab === "due" && due.map(item => (
              <div key={item.key} className="flex items-start gap-2 border-b px-4 py-2.5">
                <CalendarClock className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", item.overdue ? "text-red-600" : "text-amber-600")} />
                <button type="button" className="min-w-0 flex-1 text-left" onClick={() => { setOpen(false); onOpen(item.key) }}>
                  <p className="truncate text-sm font-medium">{item.title}</p>
                  <p className="text-[11px] text-muted-foreground"><span className="capitalize">{item.kind}</span> · {item.detail}</p>
                </button>
                {onVerify && (
                  <Button size="sm" variant="outline" className="h-7 shrink-0 gap-1 px-2 text-xs" onClick={() => onVerify(item.key)} title="Mark as checked and still accurate">
                    <BadgeCheck className="h-3 w-3" /> Verified
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* One change already has its own Publish button */}
          {canPublish && tab === "pending" && items.length > 1 && (
            <div className="border-t p-3">
              <Button className="w-full gap-1.5" onClick={() => { onPublishAll(); setOpen(false) }}>
                <Upload className="h-4 w-4" /> Publish all {items.length}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}

// Ids of records with unpublished changes in the open section, for the tree markers
export const PendingIdsContext = createContext<Set<string>>(new Set())

// A small marker for tree rows with unpublished changes
export function PendingDot({ className }: { className?: string }) {
  return <span title="Unpublished changes" className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500", className)} />
}
